/* -------------------------------------------------------------------------- */
/*                                Depdendencies                               */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useTranslation } from 'react-i18next';

// UI lib components
import { Button, Group, Text } from '@mantine/core';

// UI local components
import ResourceIcon from '@features/Resource/ResourceIcon';

// Types
import {
  SearchEntityType,
  ResourceFileType,
  ResourceType,
} from '@app-types/blackroom-api';
import { ResourceMimeType } from '@app-types/blackroom-app/resourceMimeType';
import { SearchFiltersProps } from './types';

// Assets
import {
  IconPhoto as IconFilterImage,
  IconMessageQuestion,
  IconProps,
} from '@tabler/icons-react';
import { ICON_SIZE_MAP } from '@ui-kit/icons-sizes';
import { palette } from '@ui-kit/colors';
import { rem } from '@mantine/core';
import { fontSizes } from '@app/shared/themes/base';

// Constants
const SHARED_ICON_PROPS: IconProps = {
  size: 17,
  color: palette.blackroomLightGray6,
};

const ALL_SEARCH_FILTERS: {
  type: SearchEntityType | ResourceFileType;
  icon: React.JSX.Element;
}[] = [
  {
    type: ResourceFileType.Image,
    icon: <IconFilterImage {...SHARED_ICON_PROPS} />,
  },
  {
    type: ResourceFileType.Document,
    icon: (
      <ResourceIcon
        resource={{ type: ResourceType.File, mimeType: ResourceMimeType.doc }}
        size={ICON_SIZE_MAP.small}
        withColor
      />
    ),
  },
  {
    type: ResourceFileType.Pdf,
    icon: (
      <ResourceIcon
        resource={{ type: ResourceType.File, mimeType: ResourceMimeType.pdf }}
        size={ICON_SIZE_MAP.small}
        withColor
      />
    ),
  },
  {
    type: ResourceFileType.Spreadsheet,
    icon: (
      <ResourceIcon
        resource={{ type: ResourceType.File, mimeType: ResourceMimeType.xlsx }}
        size={ICON_SIZE_MAP.small}
        withColor
      />
    ),
  },
  {
    type: ResourceFileType.Presentation,
    icon: (
      <ResourceIcon
        resource={{ type: ResourceType.File, mimeType: ResourceMimeType.pptx }}
        size={ICON_SIZE_MAP.small}
        withColor
      />
    ),
  },
  {
    type: SearchEntityType.Question,
    icon: <IconMessageQuestion size={ICON_SIZE_MAP.smaller} />,
  },
];

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function SearchFilters({
  activeFilters = [],
  onClickFilter,
  ...props
}: SearchFiltersProps) {
  /* ********************************** HOOKS ********************************* */

  // Localization
  const { t } = useTranslation('Search');

  /* ******************************** RENDERING ******************************* */
  return (
    <Group gap={rem(10)} {...props}>
      {ALL_SEARCH_FILTERS.map((f) => (
        <Button
          key={f.type}
          px="0.5rem"
          size="sm"
          h={34}
          style={{
            paddingLeft: 7,
            paddingRight: 7,
          }}
          styles={{
            root: {
              borderColor: palette.blackroomLightGray3,
            },
            section: { margin: 0 },
            inner: { gap: rem(7) },
          }}
          c={palette.blackroomLightGray6}
          variant={activeFilters.includes(f.type) ? 'light' : 'outline'}
          radius="xs"
          leftSection={f.icon}
          onClick={() => onClickFilter(f.type)}
        >
          <Text c={palette.blackroomLightGray6} fw="normal" fz={fontSizes.sm}>
            {t(`SearchFilters.filters.${f.type.toLowerCase()}`)}
          </Text>
        </Button>
      ))}
    </Group>
  );
}

export default SearchFilters;
